/**
 * Hospital Pipeline - Drag and Drop Pipeline View for Hospital Onboarding
 * Stages: Pending → Verified → Active → Premium
 */

import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Building2,
  Users,
  Mail,
  Phone,
  MapPin,
  GripVertical,
  Clock,
  CheckCircle2,
  Shield,
  Star,
  ExternalLink,
  RefreshCw,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Pipeline stages for hospitals
const HOSPITAL_STAGES = [
  { 
    id: "pending", 
    label: "Pending", 
    color: "bg-amber-500",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    description: "Awaiting verification"
  },
  { 
    id: "verified", 
    label: "Verified", 
    color: "bg-blue-500",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    description: "Account verified"
  },
  { 
    id: "active", 
    label: "Active", 
    color: "bg-emerald-500",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
    description: "Currently active"
  },
  { 
    id: "premium", 
    label: "Premium", 
    color: "bg-purple-500",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    description: "Premium tier"
  }
];

// Draggable Hospital Card
const HospitalCard = ({ hospital, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: hospital.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow cursor-move group"
      data-testid={`hospital-card-${hospital.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          {...attributes}
          {...listeners}
          className="text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing mt-1"
        >
          <GripVertical className="w-4 h-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-slate-500" />
            <h4 className="font-medium text-slate-900 truncate">
              {hospital.name || hospital.hospital_name}
            </h4>
          </div>
          
          {hospital.primary_contact_email && (
            <div className="flex items-center gap-1 text-sm text-slate-500">
              <Mail className="w-3 h-3" />
              <span className="truncate">{hospital.primary_contact_email}</span>
            </div>
          )}
          
          {(hospital.city || hospital.billing_city) && (
            <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
              <MapPin className="w-3 h-3" />
              <span>{hospital.city || hospital.billing_city}, {hospital.state || hospital.billing_state}</span>
            </div>
          )}
          
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
            {hospital.users_count !== undefined && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {hospital.users_count} users
              </span>
            )}
            {hospital.created_at && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(hospital.created_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Pipeline Column
const PipelineColumn = ({ stage, hospitals, onHospitalClick }) => {
  const count = hospitals.length;
  
  return (
    <div className={`flex-1 min-w-[280px] ${stage.bgColor} rounded-xl border ${stage.borderColor} p-4`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${stage.color}`} />
          <h3 className="font-semibold text-slate-900">{stage.label}</h3>
          <span className="px-2 py-0.5 bg-white rounded-full text-xs font-medium text-slate-600">
            {count}
          </span>
        </div>
      </div>
      
      <p className="text-xs text-slate-500 mb-3">{stage.description}</p>
      
      <SortableContext
        items={hospitals.map(h => h.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3 min-h-[200px]">
          {hospitals.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              No hospitals in this stage
            </div>
          ) : (
            hospitals.map((hospital) => (
              <div key={hospital.id} onClick={() => onHospitalClick?.(hospital)}>
                <HospitalCard hospital={hospital} />
              </div>
            ))
          )}
        </div>
      </SortableContext>
    </div>
  );
};

export default function HospitalPipeline({ onHospitalClick, onRefresh }) {
  const { token } = useAuth();
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch hospitals
  const fetchHospitals = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/hospital/health-systems`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Map hospitals with their pipeline_status (default to 'active' if not set)
        const mappedHospitals = (data.health_systems || []).map(hs => ({
          ...hs,
          pipeline_status: hs.pipeline_status || (hs.is_active ? 'active' : 'pending')
        }));
        setHospitals(mappedHospitals);
      }
    } catch (error) {
      console.error("Failed to fetch hospitals:", error);
      toast.error("Failed to load hospitals");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHospitals();
  }, [fetchHospitals]);

  // Handle status change
  const handleStatusChange = async (hospitalId, newStatus) => {
    try {
      const res = await fetch(`${API}/hospital/health-systems/${hospitalId}/pipeline-status`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ pipeline_status: newStatus })
      });

      if (res.ok) {
        toast.success(`Hospital moved to ${newStatus}`);
        // Update local state
        setHospitals(prev => prev.map(h => 
          h.id === hospitalId ? { ...h, pipeline_status: newStatus } : h
        ));
        onRefresh?.();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      console.error("Status update failed:", error);
      toast.error("Failed to update status");
    }
  };

  // Drag handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const hospitalId = active.id;
    const newStatus = over.id;

    // Find which stage was dropped on
    const targetStage = HOSPITAL_STAGES.find(s => s.id === newStatus);
    if (!targetStage) {
      // Might have dropped on another hospital card - find its stage
      const targetHospital = hospitals.find(h => h.id === newStatus);
      if (targetHospital) {
        const targetStageId = targetHospital.pipeline_status;
        const currentHospital = hospitals.find(h => h.id === hospitalId);
        if (currentHospital && currentHospital.pipeline_status !== targetStageId) {
          handleStatusChange(hospitalId, targetStageId);
        }
      }
      return;
    }

    const currentHospital = hospitals.find(h => h.id === hospitalId);
    if (currentHospital && currentHospital.pipeline_status !== newStatus) {
      handleStatusChange(hospitalId, newStatus);
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    // Check if dropping over a stage column
    const targetStage = HOSPITAL_STAGES.find(s => s.id === over.id);
    if (targetStage) return;

    // If dropping over another hospital, find its stage
    const overHospital = hospitals.find(h => h.id === over.id);
    if (!overHospital) return;

    const activeHospital = hospitals.find(h => h.id === active.id);
    if (!activeHospital) return;

    // If in different stages, we'll handle in dragEnd
  };

  // Get active hospital for drag overlay
  const activeHospital = activeId ? hospitals.find(h => h.id === activeId) : null;

  // Group hospitals by stage
  const getHospitalsByStage = (stageId) => {
    return hospitals.filter(h => h.pipeline_status === stageId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Hospital Pipeline</h2>
          <p className="text-sm text-slate-500">{hospitals.length} total hospitals</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchHospitals}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Pipeline */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          {HOSPITAL_STAGES.map((stage) => (
            <PipelineColumn
              key={stage.id}
              stage={stage}
              hospitals={getHospitalsByStage(stage.id)}
              onHospitalClick={onHospitalClick}
            />
          ))}
        </div>

        <DragOverlay>
          {activeHospital ? (
            <div className="bg-white rounded-lg border-2 border-blue-500 p-4 shadow-xl">
              <HospitalCard hospital={activeHospital} isDragging />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
